import * as React from 'react'

import { styled } from '@mui/material/styles'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListSubheader from '@mui/material/ListSubheader'
import ListItemText from '@mui/material/ListItemText'
import ListItem from '@mui/material/ListItem'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import Add from '@mui/icons-material/Add'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'

import { HeaderBarHeightPixel } from 'components/atomic/organisms/header'
import WikiFiltersModal from 'pages/wiki/filters-modal'

export interface Filter {
    title: string
    items: FilterItem[]
}

export interface FilterItem {
    slug: string
    title: string
    count: number
    tags: string[]
}

/** Filters Item */

interface ItemProps {
    item: FilterItem
    handleToggle: (slug: string) => () => void
    selected: boolean
}

const Item = React.memo(({ item, handleToggle, selected }: ItemProps) => {
    return (
        <ListItem
            key={item.slug}
            secondaryAction={
                <Stack alignItems={'center'}>
                    <Chip size="small" label={item.count} sx={{ fontWeight: 'bold' }} />
                </Stack>
            }
            sx={{ paddingX: '8px' }}
            disablePadding>
            <ListItemButton
                sx={{ borderRadius: '8px', paddingY: '2px', paddingX: '8px' }}
                onClick={handleToggle(item.slug)}
                dense>
                <ListItemIcon sx={{ minWidth: 0 }}>
                    <Checkbox
                        edge="start"
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{}}
                        sx={{ paddingY: 0 }}
                        size="small"
                    />
                </ListItemIcon>
                <ListItemText primary={item.title} />
            </ListItemButton>
        </ListItem>
    )
})

/** Filters Group */

interface FilterGroupProps {
    title: string
    items: FilterItem[]
    activeFilters: FilterItem[]
    onClear: () => void
    onAdd: (item: FilterItem) => void
    onRemove: (slug: string) => void
}

export const FilterGroup = React.memo((props: FilterGroupProps) => {
    const FILTER_GROUP_ITEMS_NUMBER = 10

    const [open, setOpen] = React.useState(false)

    const handleClear = () => {
        props.onClear()
    }

    const handleToggle = React.useCallback(
        (slug: string) => () => {
            const exists = props.activeFilters.find((item) => item.slug === slug)

            if (exists) {
                props.onRemove(slug)
            } else {
                const item = props.items.find((item) => item.slug === slug) as FilterItem
                props.onAdd(item)
            }
        },
        [props],
    )

    const renderList = React.useCallback(() => {
        return (
            <List>
                {props.items.slice(0, FILTER_GROUP_ITEMS_NUMBER).map((item) => (
                    <Item
                        key={item.slug}
                        item={item}
                        handleToggle={handleToggle}
                        selected={!!props.activeFilters.find((f) => f.slug === item.slug)}
                    />
                ))}
            </List>
        )
    }, [props.items, handleToggle, props.activeFilters])

    const hasActiveFilters = React.useMemo(
        () => props.activeFilters.some((f) => props.items.some((i) => i.slug === f.slug)),
        [props.activeFilters, props.items],
    )

    return (
        <Box>
            <ListSubheader
                sx={{
                    justifyContent: 'space-between',
                    display: 'flex',
                    alignItems: 'center',
                    paddingX: '16px',
                    lineHeight: 'inherit',
                    minHeight: '48px',
                    position: 'unset',
                    pt: 3,
                }}>
                {props.title}

                {hasActiveFilters ? (
                    <Chip size="small" onClick={handleClear} onDelete={handleClear} label={'Clear'} />
                ) : null}
            </ListSubheader>

            {renderList()}

            {props.items.length > FILTER_GROUP_ITEMS_NUMBER ? (
                <Box px={0}>
                    <ListItem sx={{ paddingX: '8px' }} disablePadding>
                        <ListItemButton
                            sx={{ borderRadius: '8px', paddingY: '2px', paddingLeft: '4px' }}
                            onClick={() => setOpen(true)}
                            dense>
                            <ListItemIcon sx={{ minWidth: 0, paddingRight: '8px' }}>
                                <Add fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={'Mostra altri'} />
                        </ListItemButton>
                    </ListItem>
                </Box>
            ) : null}

            <Box px={2} pt={2}>
                <Divider />
            </Box>

            <WikiFiltersModal
                title={props.title}
                open={open}
                onClose={() => setOpen(false)}
                items={props.items}
                activeFilters={props.activeFilters}
                onAdd={props.onAdd}
                onRemove={props.onRemove}
            />
        </Box>
    )
})

/** Filters Panel */

const FiltersPanelWrapper = styled(Box)`
    align-self: flex-start;
    position: sticky;
    top: ${HeaderBarHeightPixel}px;
    width: 300px;
    min-width: 300px;
    margin-left: -15px;
    overflow: auto;
    max-height: calc(100vh - ${HeaderBarHeightPixel}px);
`

export default function FiltersPanel({ children }: { children: React.ReactNode }) {
    return <FiltersPanelWrapper display={{ xs: 'none', md: 'block' }}>{children}</FiltersPanelWrapper>
}
